import React, { FC, useState } from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import {
  ClickablePrimaryApplicationIcon,
  CsPrimaryApplicationIcon
} from '../../legacy-design-system/icons'
import { theme } from '../../styled'
import { UnmergeDialog } from './unmerge-dialog'
import { PureQueryOptions } from 'apollo-client'
import { WithTooltip, tooltipTitleCss } from '../../design-system'
import { Role } from '@contrast-security-inc/base-types'

const boldCss = css`
  font-weight: ${theme.fontWeights.secondary.boldest};
`

const tooltipContainerCss = css`
  max-width: 260px;
`

const primaryApplicationIndicatorTestId =
  'primary-application-indicator-test-id'

export const TooltipContent: FC<{
  moduleCount: number
  clickable?: boolean
}> = ({ moduleCount, clickable = false }) => {
  const intl = useIntl()

  return (
    <div className={tooltipContainerCss}>
      <Text as="div" font="metadataStrong" className={tooltipTitleCss}>
        {intl.formatMessage({ id: 'title_primaryApplication' })}
      </Text>
      <Text as="div">
        {intl.formatMessage({ id: 'detail_applicationWasMerged' })}
      </Text>
      <span className={boldCss}>{moduleCount} </span>
      <Text as="span">
        {intl.formatMessage(
          { id: 'plural_representedApplicationModules' },
          { count: moduleCount }
        )}
      </Text>
      {clickable && (
        <Text as="div">
          {intl.formatMessage(
            { id: 'variable_applicationPrimarySeeModulesOrLearnMoreComponent' },
            {
              learnMore: (
                <Link
                  href={intl.formatMessage({ id: 'link_mergeApplications' })}
                  isExternal
                >
                  {intl.formatMessage({ id: 'detail_learnMore' })}
                </Link>
              )
            }
          )}
        </Text>
      )}
    </div>
  )
}

export const PrimaryApplicationClickableIndicator: FC<{
  organizationID: string
  organizationRole: Role
  primaryApplication: {
    id: string
    name: string
    licensed: boolean
    role: Role
  }
  moduleCount: number
  refetchQueries?: PureQueryOptions[]
  refresh?: () => Promise<any>
}> = ({
  moduleCount,
  primaryApplication,
  organizationID,
  organizationRole,
  refetchQueries,
  refresh
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const onClick = () => setIsOpen(true)

  const closeDialog = () => setIsOpen(false)

  return (
    <WithTooltip
      children={() => {
        return (
          <>
            <ClickablePrimaryApplicationIcon
              data-testid={primaryApplicationIndicatorTestId}
              onClick={onClick}
            />
            <UnmergeDialog
              isOpen={isOpen}
              closeDialog={closeDialog}
              primaryApplication={primaryApplication}
              moduleCount={moduleCount}
              organizationID={organizationID}
              organizationRole={organizationRole}
              refetchQueries={refetchQueries}
              refresh={refresh}
            />
          </>
        )
      }}
      renderTooltipContent={() => {
        return <TooltipContent moduleCount={moduleCount} clickable={true} />
      }}
    />
  )
}

export const PrimaryApplicationIndicator: FC<{
  moduleCount: number
  containerClassName?: string
}> = ({ moduleCount, containerClassName }) => (
  <WithTooltip
    width="auto"
    containerClassName={containerClassName}
    children={() => <CsPrimaryApplicationIcon />}
    renderTooltipContent={() => <TooltipContent moduleCount={moduleCount} />}
  />
)
